import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import type { IntakeFee } from '../interfaces';
import type { FieldSchema } from '@FormModule';
import { INTAKE_TYPES } from '../enums';
import { useAToast } from '@Atoms';
import { applyShelterCurrency } from '@Helpers';
import { useSessionStore } from '@Stores';
import { getIntakeFlags } from '@AnimalModule';
import { AddIntakeAnimalToCart, AddPeopleToIntakeCartService, INTAKES_WITH_PAYMENT, useCartStore } from '@CartModule';

interface PartnerPayload {
  _id: string;
  name: string;
}

/* NOTE
 * There is a couple of blocks that will need to be implemented directly in the
 * target form since they have dependencies on other fields and state.

:: import { useFormIntakeFeeFields } from '@IntakeModule';
  const {
    intakeCartAnimals,
    intakeFees,
    intakeForm,
    intakeWaiversSelect,
    intakeWaiversToggleSelect,
    waivers,

    getIntakeFeesSelectRawField,
    getMappedIntakeFeesValue,
    setCartPayload,
    onBeforeCartSubmission,
  } = useFormIntakeFeeFields();
 
 :: positionStaticFields()
    if (intakeWaiversSelect) {
      formFields.splice(2, 0, intakeWaiversToggleSelect);
      formFields.splice(3, 0, intakeWaiversSelect);
      if (intakeFeesSelect) formFields.splice(4, 0, intakeFeesSelect); // NOTE :: intake fee field could be disabled
    }
 
 :: needsSignatureModel
    const needsSignatureModel = computed(() => fieldsSchema.value.find(({ name }) => name === 'needs_waivers')?.model);
    watch(needsSignatureModel, () => onShowNeedsSignatureField(fieldsSchema.value, needsSignatureModel.value));

  :: prior to people form setup
    setCartPayload()

  :: after people form submission
    if (intakeCartAnimals.value.length) {
      const person = {
        _id: personOid.$oid,
        ...PAYLOAD,
      };

      onBeforeCartSubmission(formId.value, formType.value, formTitle.value, person);

      return;
    }
 */

const formNameIntake = ref();
const intakeCartAnimals = ref<any[]>([]);
const intakeFees = ref<IntakeFee[]>([]);
const intakeForm = ref<any>();
const waivers = ref<any[]>([]);

export default function () {
  const { cartAside, cartData, cartId, emptyCart } = storeToRefs(useCartStore());
  const { fetchActiveCart } = useCartStore();
  const { shelterProfile } = storeToRefs(useSessionStore());

  const { notify, TOAST_TYPES } = useAToast();
  const { push } = useRouter();

  // FIELD SETTINGS
  const intakeFeeSettings = {
    edit: {
      label: 'label',
    },
    settings: {
      type: 'select',
    },
  };
  const intakeDateSettings = {
    model: new Date(),
  };
  const needsWaiversSettings = {
    model: true,
    value: true,
    state: {
      disabled: false,
      errors: new Set<string>(),
    },
  };
  const intakeWaiversSettings = {
    model: [],
    edit: {
      label: 'label',
      options: [],
    },
    settings: {
      type: 'intake_waivers',
    },
  };

  // NOTE :: Shelter Info intake flags
  const showNeedsSignatureField = computed(() => {
    // prettier-ignore
    return formNameIntake.value === INTAKE_TYPES.STRAY ? !!shelterProfile.value?.enable_intake_stray_signature
      : formNameIntake.value === INTAKE_TYPES.BOARDING_IN ? !!shelterProfile.value?.enable_intake_boarding_in_signature
      : formNameIntake.value === INTAKE_TYPES.CLINIC_IN ? !!shelterProfile.value?.enable_intake_clinic_in_signature
      : formNameIntake.value === INTAKE_TYPES.DOA ? !!shelterProfile.value?.enable_intake_doa_signature
      : formNameIntake.value === INTAKE_TYPES.OWNER_REQUESTED_EUTHANASIA ? !!shelterProfile.value?.enable_intake_owner_requested_euthanasia_signature
      : formNameIntake.value === INTAKE_TYPES.OWNER_SURRENDER ? !!shelterProfile.value?.enable_intake_owner_surrender_signature
      : formNameIntake.value === INTAKE_TYPES.RETURN ? !!shelterProfile.value?.enable_intake_return_signature
      : formNameIntake.value === INTAKE_TYPES.SEIZED ? !!shelterProfile.value?.enable_intake_seized_signature
      : formNameIntake.value === INTAKE_TYPES.SERVICE_IN ? !!shelterProfile.value?.enable_intake_service_in_signature
      : formNameIntake.value === INTAKE_TYPES.TRANSFER_IN ? !!shelterProfile.value?.enable_intake_transfer_in_signature
      : formNameIntake.value === INTAKE_TYPES.WILDLIFE_IN ? !!shelterProfile.value?.enable_intake_wildlife_in_signature
      : false;
  });

  const showIntakeFeeField = computed(() => {
    // prettier-ignore
    return formNameIntake.value === INTAKE_TYPES.STRAY ? !!shelterProfile.value?.enable_intake_stray_payment
      : formNameIntake.value === INTAKE_TYPES.BOARDING_IN ? !!shelterProfile.value?.enable_intake_boarding_in_payment
      : formNameIntake.value === INTAKE_TYPES.CLINIC_IN ? !!shelterProfile.value?.enable_intake_clinic_in_payment
      : formNameIntake.value === INTAKE_TYPES.DOA ? !!shelterProfile.value?.enable_intake_doa_payment
      : formNameIntake.value === INTAKE_TYPES.OWNER_REQUESTED_EUTHANASIA ? !!shelterProfile.value?.enable_intake_owner_requested_euthanasia_payment
      : formNameIntake.value === INTAKE_TYPES.OWNER_SURRENDER ? !!shelterProfile.value?.enable_intake_owner_surrender_payment
      : formNameIntake.value === INTAKE_TYPES.RETURN ? !!shelterProfile.value?.enable_intake_return_payment
      : formNameIntake.value === INTAKE_TYPES.SEIZED ? !!shelterProfile.value?.enable_intake_seized_payment
      : formNameIntake.value === INTAKE_TYPES.SERVICE_IN ? !!shelterProfile.value?.enable_intake_service_in_payment
      : formNameIntake.value === INTAKE_TYPES.TRANSFER_IN ? !!shelterProfile.value?.enable_intake_transfer_in_payment
      : formNameIntake.value === INTAKE_TYPES.WILDLIFE_IN ? !!shelterProfile.value?.enable_intake_wildlife_in_payment
      : false;
  });

  // NOTE :: Shelter Admin
  const hasIntakeFeesAndWaivers = computed(() => shelterProfile.value?.enable_intake_fees);

  function getIntakeFeesSelectRawField(formName: string) {
    if (!hasIntakeFeesAndWaivers.value) return null;

    if (!INTAKES_WITH_PAYMENT.includes(formName as INTAKE_TYPES)) return null;

    if (!showIntakeFeeField.value) return null;

    return {
      label: 'Fee',
      name: 'intake_fee',
      required: true,
      type: 'select',
      value: '',
      values: intakeFees.value || [],
      intakeField: true,
    };
  }

  function getWaiverSelectRawField(formName: string) {
    if (!hasIntakeFeesAndWaivers.value) return null;

    if (!INTAKES_WITH_PAYMENT.includes(formName as INTAKE_TYPES)) return null;

    if (!showNeedsSignatureField.value) return null;

    return {
      label: 'Waivers',
      name: 'intake_waivers',
      required: true,
      type: 'intake_waivers',
      value: '',
      values: waivers.value || [],
      intakeField: true,
    };
  }

  function getWaiverToggleSelectRawField(formName: string) {
    if (!hasIntakeFeesAndWaivers.value) return null;

    if (!INTAKES_WITH_PAYMENT.includes(formName as INTAKE_TYPES)) return null;

    if (!showNeedsSignatureField.value) return null;

    const feesForType = intakeFees.value.filter((fee: IntakeFee) => fee.intake_type === formName);
    const hasIntakeFees = feesForType.length > 0;
    const hasIntakeWaivers = waivers.value.length > 0;

    const hasFeesEnabled = showIntakeFeeField.value && hasIntakeFees;
    const hasWaiversEnabled = showNeedsSignatureField.value && hasIntakeWaivers;

    const label =
      hasFeesEnabled && hasWaiversEnabled
        ? 'animals.individualPage.animalAdmission.requireWaiversAndFees'
        : 'animals.individualPage.animalAdmission.requireWaivers';

    return {
      label,
      name: 'needs_waivers',
      required: false,
      type: 'toggle',
      value: '',
      values: [],
      intakeField: true,
    };
  }

  function getMappedIntakeFeesValue(result: any, DEFAULT_FORM: string, formName: string) {
    if (!result?.intake_fees?.length) return [];

    const mappedIntakeFees = result.intake_fees
      .map((fee: any) => {
        return {
          ...fee,
          label: `${fee.title} - ${applyShelterCurrency(fee.fee)}`,
        };
      })
      .filter((fee: any) => fee.species === DEFAULT_FORM && fee.intake_type === formName);

    return mappedIntakeFees;
  }

  function getShelterPaymentIntakeValidation(formName?: string) {
    const intakeType = formName || formNameIntake.value;
    const flags = getIntakeFlags(intakeType, shelterProfile.value);

    return {
      payment: flags.payment,
      signature: flags.signature,
    };
  }

  function setCartPayload(PAYLOAD: any, animals: any[]) {
    const needsCartPayment = PAYLOAD?.needs_waivers;
    if (!needsCartPayment) return false;

    const { payment, signature } = getShelterPaymentIntakeValidation();
    const noCartPayment = !payment && !signature;
    if (noCartPayment) return false;
    intakeCartAnimals.value = [];

    animals.forEach((animal, index) => {
      const cartPayload = {
        ...PAYLOAD,
        animal_name: animal.animal_name || PAYLOAD.animal_name,
        additional_fees: [],
        animal_id: animal.animal_id.$oid,
        id: animal.id,
        cart_id: cartId.value,
        cart_type: 'intake',
        form_id: PAYLOAD.formId,
        intake_fee: !payment && signature ? 0 : PAYLOAD?.intake_fee?.fee || 0,
        intake_fee_details: PAYLOAD?.intake_fee || null,
        fee_name: PAYLOAD?.intake_fee?.title || null,
        intake_id: animal.intake_id,
        intake_type: PAYLOAD.form_name,
        has_fees: showIntakeFeeField.value, //  Flag for adopt app
        litter_id: !shelterProfile.value?.enable_groups_litters ? animal.litter_id?.$oid : null,
        outcome_name: PAYLOAD.form_name,
        outcome_title: PAYLOAD.intake_type,
        outcome_type: PAYLOAD.form_name,
        photo: '/storage/paw2.png',
        product: 'animal',
        microchip: index === 0 ? PAYLOAD?.microchip || '' : '',
        outcome: {
          animal_id: animal.animal_id.$oid,
          form_id: PAYLOAD.formId,
          form_table: PAYLOAD.formTable,
          form_title: PAYLOAD.intake_type,
          outcome_date: PAYLOAD.intake_date,
          outcome_title: PAYLOAD.intake_type,
          outcome_type: PAYLOAD.form_name,
        },
      };

      intakeCartAnimals.value.push(cartPayload);
    });

    return !!intakeCartAnimals.value.length;
  }

  function onShowNeedsSignatureField(fieldsSchema: FieldSchema[], needsSignatureModel: boolean) {
    fieldsSchema.forEach((field) => {
      const targetFields = ['intake_fee', 'needs_waivers', 'intake_waivers'];
      if (!targetFields.includes(field.name)) return;

      switch (field.name) {
        case 'needs_waivers': {
          field.settings = {
            ...field.settings,
            show: showNeedsSignatureField.value,
          };

          break;
        }

        case 'intake_fee': {
          const showField = needsSignatureModel && showNeedsSignatureField.value;

          field.settings = {
            ...field.settings,
            show: showField,
            required: showField,
          };

          if (!showField) {
            field.model = '';
            field.value = '';
            field.state.errors.clear();
          }
          break;
        }

        case 'intake_waivers': {
          const showField = needsSignatureModel;

          field.settings = {
            ...field.settings,
            show: showField,
            required: showField,
          };

          if (!needsSignatureModel) {
            field.model = [];
            field.value = [];
            field.state.errors.clear();
          }

          break;
        }
      }
    });
  }

  async function validateIntakeGoingToCart(formName: string) {
    const hasPaymentFlow = INTAKES_WITH_PAYMENT.includes(formName as INTAKE_TYPES);
    if (!hasPaymentFlow) return true;

    await fetchActiveCart();

    if (emptyCart.value) return true;

    const { signature, payment } = getShelterPaymentIntakeValidation(formName);
    if (!signature && !payment) return true;

    const CART_NOT_EMPTY = {
      message: 'cart.toast.intakeFlowAttempONotEmptyCart.error.message',
      title: 'cart.toast.intakeFlowAttempONotEmptyCart.error.title',
      type: TOAST_TYPES.ERROR,
    };

    notify(CART_NOT_EMPTY);
    cartAside.value = true;
    return false;
  }

  async function onAddIntakeToCart(resetCart: boolean = true, partner: PartnerPayload | null = null) {
    if (resetCart) useCartStore().resetCart();

    const PAYLOAD = {
      animals: intakeCartAnimals.value,
      cart_type: 'intake' as const,
      form_id: intakeForm.value.id,
      has_fees: showIntakeFeeField.value, // Flag for adopt app
      outcome_title: intakeForm.value.title,
      outcome_type: formNameIntake.value,
      partner,
      resetCart,
    };

    return await AddIntakeAnimalToCart(PAYLOAD).then((data) => {
      cartData.value = data;
      return data;
    });
  }

  async function onAddIntakePeopleToCart(payload: any) {
    const PAYLOAD = {
      person: payload,
      cart_id: cartId.value,
    };

    await AddPeopleToIntakeCartService(PAYLOAD).then(async (cart) => {
      cartData.value = cart;
      await push(`/shelter/animal/intake-payment/${cartId.value}`);
    });
  }

  function resetFormIntakeFields() {
    intakeFees.value = [];
    intakeCartAnimals.value = [];
    intakeForm.value = null;
    waivers.value = [];
  }

  return {
    intakeCartAnimals,
    intakeDateSettings,
    intakeFeeSettings,
    intakeForm,
    intakeWaiversSettings,
    needsWaiversSettings,
    showNeedsSignatureField,

    // REFS
    intakeFees,
    formNameIntake,
    waivers,

    // HELPERS
    getIntakeFeesSelectRawField,
    getWaiverSelectRawField,
    getWaiverToggleSelectRawField,
    getMappedIntakeFeesValue,
    setCartPayload,
    resetFormIntakeFields,
    validateIntakeGoingToCart,

    // EVENTS
    onAddIntakeToCart,
    onAddIntakePeopleToCart,
    onShowNeedsSignatureField,
  };
}
